/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit'
import { BranchingOperation, type IBranchingSurveyItems, type Question } from '../../../features/SurveyTempleteFeature/types'
import { createSurveyTemplate, editSurveyTemplate, getSurveyByIdApi } from './apis'
import { notification } from '../../../components/Notifications'
import { v4 as uuidv4 } from 'uuid'
import { preparedBranchFilterForUpdateFilters, updateBranchingFiltersSectionNameInParentSection } from '../../../features/SurveyTempleteFeature/helpers'
export interface IEditableQuestion {
  questionIndex: number
  sectionIndex: number
  question: Question
}

export interface IAttribute {
  key: string
  op: string
  type: string
  values: string[]
  id?: string
}

export interface ISurveySection {
  name: string
  title?: string | null
  descr?: string | null
  surveySectionId: string
  isRepeatPerInstructor?: boolean
  attributes?: IAttribute[]
  surveyItems?: Question[]
  indentation: number
  branchingFilters: Record<string, any> | null
  parentSurveySectionId: string | null
  revision?: number
}

export interface ISelectedQuestion {
  sectionIndex: number
  questionType: string | null
  question?: Question
}

const initialSection: ISurveySection = {
  name: 'Untitled section 1',
  isRepeatPerInstructor: false,
  indentation: 0,
  surveySectionId: uuidv4(),
  parentSurveySectionId: null,
  branchingFilters: null
}

interface ISurveyTemplate {
  selectedQuestion: ISelectedQuestion | null
  selectedSection: ISurveySection | null
  title: string
  surveySections: ISurveySection[]
  loading: boolean
  actionLoading: boolean
  editableQuestion: IEditableQuestion | null | undefined
  editableSurvey: ISurveySection | null
  branchModal: boolean
}

const initialState: ISurveyTemplate = {
  selectedQuestion: null,
  selectedSection: null,
  title: '',
  surveySections: [initialSection],
  loading: false,
  actionLoading: false,
  editableQuestion: null,
  editableSurvey: null,
  branchModal: false
}

const surveyTemplateSlice = createSlice({
  name: 'surveyTemplate',
  initialState,
  reducers: {
    resetState (state) {
      const newInitialSection = {
        ...initialSection,
        surveySectionId: uuidv4()
      }
      Object.assign(state, {
        ...initialState,
        surveySections: [newInitialSection]
      })
    },
    setTitle (state, action) {
      state.title = action.payload
    },
    addSection (state) {
      state.surveySections?.push({
        surveySectionId: uuidv4(),
        name: `Untitled section ${state.surveySections.length + 1}`,
        isRepeatPerInstructor: false,
        indentation: 0,
        parentSurveySectionId: null,
        branchingFilters: null
      })
    },
    removeSection (state, action: PayloadAction<{ section: ISurveySection }>) {
      const { section } = action.payload
      // Remove the current section from the main surveySections array
      state.surveySections = state.surveySections.filter((s: ISurveySection) => s.surveySectionId !== section.surveySectionId)
      state.selectedSection = null

      // If a parentSurveySectionId exists, we need to clean up the branchingFilters in that section linked surveyItem as well
      if (section.parentSurveySectionId) {
        const surveySections = state.surveySections ?? []
        const parentSurveySection = surveySections.find((s: ISurveySection) => s.surveySectionId === section.parentSurveySectionId)
        if (parentSurveySection?.surveyItems) {
          parentSurveySection.surveyItems?.forEach((item) => {
            if (item.branchingFilters) {
              item.branchingFilters = item.branchingFilters.filter(
                (filter) => filter.surveySectionId !== section.surveySectionId
              )
            }
          })
        }
      }
    },

    setSectionName (state, action) {
      const { surveySectionId, value } = action.payload
      const findSectionById = (sections: any[], id: any): any => sections.find((section) => section.surveySectionId === id)

      const sectionToUpdate = findSectionById(state.surveySections, surveySectionId)
      if (!sectionToUpdate) return

      sectionToUpdate.name = value
      // If Display title is Empty then Update Display title for The First time
      if (!sectionToUpdate.title) {
        sectionToUpdate.title = value
      }

      // Find the parent section
      const parentSurveySection = findSectionById(state.surveySections, sectionToUpdate.parentSurveySectionId)
      if (!parentSurveySection) return

      // Update branching filters in the parent section
      updateBranchingFiltersSectionNameInParentSection(parentSurveySection, surveySectionId, value)
    },

    setSectionTitle (state, action) {
      const { surveySectionId, value } = action.payload
      const sectionToUpdate = state.surveySections.find((section) => section.surveySectionId === surveySectionId)

      if (sectionToUpdate) {
        sectionToUpdate.title = value
      }
    },
    setSectionDescription (state, action) {
      const { surveySectionId, value } = action.payload
      const sectionToUpdate = state.surveySections.find((section) => section.surveySectionId === surveySectionId)
      if (sectionToUpdate) {
        sectionToUpdate.descr = value
      }
    },

    moveQuestionUp (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex > 0 && questionIndex < section.surveyItems.length) {
        const surveyItems = section.surveyItems
        // Swap the question with the previous one
        const temp = surveyItems[questionIndex]
        surveyItems[questionIndex] = surveyItems[questionIndex - 1]
        surveyItems[questionIndex - 1] = temp
      }
    },
    moveQuestionDown (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex < section.surveyItems.length - 1) {
        const surveyItems = section.surveyItems
        const temp = surveyItems[questionIndex]
        surveyItems[questionIndex] = surveyItems[questionIndex + 1]
        surveyItems[questionIndex + 1] = temp
      }
    },

    deleteQuestionFromSection (state, action: PayloadAction<{ surveySectionId: string | undefined, questionIndex: number }>) {
      const { surveySectionId, questionIndex } = action.payload
      const surveySections = state.surveySections ?? []

      // Find the section by surveySectionId
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)

      if (section?.surveyItems && questionIndex < section.surveyItems.length) {
        // Remove the question from the section's surveyItems array
        section.surveyItems.splice(questionIndex, 1)
      }
    },
    addSurveyQuestionInSection (state, action: PayloadAction<{ surveySectionId: string | undefined, question: Question }>) {
      const { surveySectionId, question } = action.payload
      const surveySections = state.surveySections ?? []

      // Find the section by surveySectionId
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)

      if (section) {
        // Add the new question to the section's surveyItems array
        section.surveyItems = [...(section.surveyItems ?? []), question]
      }
    },

    setSelectedQuestion (state, action) {
      state.selectedQuestion = action.payload
    },
    setSelectedSection (state, action) {
      state.selectedSection = action.payload
    },

    setfilters (state, action) {
      const { attributes, surveySectionId, branchingFilters, isRepeatPerInstructor } = action.payload
      const { filters, parentSection, parentQuestion } = branchingFilters ?? {}
      const { operation, answer, prompt } = filters ?? {}

      const section = state.surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)

      if (!section) return

      // Handle Repeat Per Instructor Filters
      if (isRepeatPerInstructor !== undefined) {
        section.isRepeatPerInstructor = isRepeatPerInstructor
      }

      // Handle Attributes Filters
      if (attributes) {
        section.attributes = attributes
      }

      // Handle Branching Filters
      if (branchingFilters) {
        const parentSurveyItems = state.surveySections.find((s: ISurveySection) => s?.surveySectionId === parentSection?.surveySectionId)?.surveyItems ?? []

        if (!parentSurveyItems.length || !parentSection.surveyItems) return

        const questionIndex = parentSection.surveyItems?.findIndex((item: Question) => item.itemId === parentQuestion.itemId)

        if (questionIndex === -1) return

        const newBranchFilter = preparedBranchFilterForUpdateFilters(operation, answer, section)

        const currentBranchingFilters = parentSurveyItems[questionIndex].branchingFilters

        const currentBranching = currentBranchingFilters?.filter(d => d.surveySectionId !== surveySectionId)
        // Update parent question with the branching filter
        parentSurveyItems[questionIndex] = {
          ...parentSurveyItems[questionIndex],
          branchingFilters: [...(currentBranching ?? []), ...(newBranchFilter ?? [])]

        }

        // Update current section's branching filters
        section.branchingFilters = { ...section.branchingFilters, operation, answer }
      }
    },

    // If the Parent Question prompt changes, we also need to update the Branch filter prompt
    updateBranchFilterPrompt (state, action) {
      const { prompt, childBranches } = action.payload

      state.surveySections.forEach((section) => {
        const isMatch = childBranches?.some(
          (_branch: IBranchingSurveyItems) => _branch.surveySectionId === section.surveySectionId
        )

        if (isMatch) {
          section.branchingFilters = {
            ...section.branchingFilters,
            prompt
          }
        }
      })
    },

    // If the Parent Question Option changes, we also need to update the Branch filter Answers
    updateBranchFilterAnswers (state, action) {
      const { oldKey, newKey, branchSurveySectionId } = action.payload
      const section = state.surveySections.find((section) => section.surveySectionId === branchSurveySectionId)
      if (section) {
        const updatedAnswers = section.branchingFilters?.answer?.map((answer: string) =>
          answer === oldKey ? newKey : answer
        )
        section.branchingFilters = {
          ...section.branchingFilters,
          answer: updatedAnswers
        }
      }
    },

    setEditableQuestion (state, action: PayloadAction<IEditableQuestion | null>) {
      if (action.payload?.question) {
        state.editableQuestion = {
          sectionIndex: action.payload?.sectionIndex,
          questionIndex: action.payload?.questionIndex,
          question: action.payload.question
        }
        return
      }
      state.editableQuestion = null
    },

    updateQuestionInSection (state, action: PayloadAction<{ surveySectionId?: string, questionIndex: number, updatedQuestion: Question }>) {
      const { surveySectionId, questionIndex, updatedQuestion } = action.payload
      const surveySections = state.surveySections ?? []
      const section = surveySections.find((s: ISurveySection) => s.surveySectionId === surveySectionId)
      if (section?.surveyItems && questionIndex < section.surveyItems.length) {
        section.surveyItems[questionIndex] = updatedQuestion
      }
      state.editableQuestion = null
    },

    setEditableSurvey (state, action) {
      state.editableSurvey = action.payload
      state.title = action.payload?.title
      state.surveySections = action.payload?.surveySections
    },
    setBranchModal (state, action) {
      state.branchModal = action.payload
    },
    addBranchSection (state, action) {
      const { parentSurveySectionId, indentation, branchingFilters, section } = action.payload
      const { operation, answer, prompt, itemType, itemId } = branchingFilters ?? {}

      const newBranchSection = {
        surveySectionId: uuidv4(),
        name: `Untitled section ${state.surveySections.length + 1}`,
        isRepeatPerInstructor: false,
        parentSurveySectionId,
        indentation: indentation ? indentation + 1 : 1,
        branchingFilters: {
          operation,
          prompt,
          itemType,
          itemId,
          answer
        }
      }

      // Add branch section after the end of the parent child section
      const parentSectionIndex = state.surveySections.findIndex(s => s.surveySectionId === parentSurveySectionId)
      if (parentSectionIndex !== -1) {
        const childSections = state.surveySections.filter(
          s => s.parentSurveySectionId === parentSurveySectionId
        )

        if (childSections.length > 0) {
          const lastChildSection = childSections[childSections.length - 1]
          const lastChildIndex = state.surveySections.findIndex(
            s => s.surveySectionId === lastChildSection.surveySectionId
          )
          state.surveySections.splice(lastChildIndex + 1, 0, newBranchSection)
        } else {
          state.surveySections.splice(parentSectionIndex + 1, 0, newBranchSection)
        }
      } else {
        state.surveySections.push(newBranchSection)
      }

      // Logic for adding BranchFilters in parent Question of this Branch Section
      const surveyItems = state.surveySections.find(s => s.surveySectionId === section.surveySectionId)?.surveyItems ?? []
      const branchFilter = preparedBranchFilterForUpdateFilters(operation, answer, newBranchSection)

      // Set branchingFilters in the corresponding parent survey item
      const parentSurveyItem = surveyItems.find(item => item.itemId === itemId)
      if (surveyItems?.length && parentSurveyItem) {
        parentSurveyItem.branchingFilters = [...(parentSurveyItem?.branchingFilters ?? []), ...branchFilter]
      }

      notification('New Branch Created', { variant: 'success', subTitle: newBranchSection.name })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSurveyByIdApi.pending, (state) => {
        state.loading = true
      })
      .addCase(getSurveyByIdApi.fulfilled, (state, action) => {
        state.loading = false
      })
      .addCase(getSurveyByIdApi.rejected, (state, action) => {
        state.loading = false
      })
      // create survey
      .addCase(createSurveyTemplate.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(createSurveyTemplate.fulfilled, (state, action) => {
        state.actionLoading = false
        notification('Survey added successfully', { variant: 'success', subTitle: action.payload?.title })
      })
      .addCase(createSurveyTemplate.rejected, (state, action) => {
        state.actionLoading = false
      })

      // update survey
      .addCase(editSurveyTemplate.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(editSurveyTemplate.fulfilled, (state, action) => {
        state.actionLoading = false
        notification('Survey Template saved', { variant: 'success', subTitle: ' Your changes have been saved successfully.' })
      })
      .addCase(editSurveyTemplate.rejected, (state, action) => {
        state.actionLoading = false
        notification(action.payload?.response?.data?.error, { variant: 'error' })
      })
  }
})

export const {
  resetState,
  setTitle,
  addSection,
  removeSection,
  setSectionName,
  setSectionTitle,
  setSectionDescription,
  addSurveyQuestionInSection,
  setSelectedQuestion,
  setSelectedSection,
  moveQuestionUp,
  moveQuestionDown,
  deleteQuestionFromSection,
  setEditableQuestion,
  updateQuestionInSection,
  setEditableSurvey,
  setfilters,
  addBranchSection,
  setBranchModal,
  updateBranchFilterPrompt,
  updateBranchFilterAnswers
} = surveyTemplateSlice.actions

export default surveyTemplateSlice.reducer
